<template>
    <TemplateModalEx v-model:show="computedShow" class="googleAccountAgentSettingDialogDetails">
        <template #title>Googleアカウントでのログイン設定（端末個別設定）</template>
        <template #body>
            <span style="margin-bottom: 16px; height:16px;">Googleアカウントでのログイン設定を個別に行う端末を登録してください。</span>
            <div class="division-area-wrapper">
                <DivisionArea ref="refDivisionArea" :enableDivisionChangeButton="true" :enableInheritDivision="true"
                    :selectedDivisionId="selectedDivisionId" :isInheritDivision="isInheritDivision"
                    @onDivisionSelectChange="onDivisionSelectChange" />
            </div>
            <ListTitleBar ref="refTitleBar" titleCaption="Googleアカウントでのログイン設定一覧" btnCaption_1="追加" btnCaption_2="編集"
                btnCaption_3="削除" :btnActiveType_1="buttonActiveTypeForAdd" :btnActiveType_2="1" :btnActiveType_3="3"
                @onBtnClicked_1="onAdd" @onBtnClicked_2="onEdit" @onBtnClicked_3="onDelete" :selectCount="selectCount"
                :rightMenuItemList="selectRightMenuItemList" :rowCount="rowCount" :rowCountArea="true"
                :isUseRightMenuItemList="true" :enableSearchText="true" @onSearchBoxKeydown="search"
                :widthSearchBox="200"
                @onBtnClear="search" @onClickRightMenuItem="onClickRightMenuItem" />
            <ListCtrl ref="refGoogleAccountListCtrl" :columns="GOOGLE_ACCOUNT_SETTING_COLUMN" :selectType="2"
                :getCellTextFunc="getCellText" class="google-account-list" />
            <div>
                <input style="display: none" id="importFileImput_GooglAccountImport" type="file" accept=".csv"
                    @change="onFileSelected">
            </div>
        </template>
        <template #footer>
            <div class="d-flex flex-row justify-content-center align-items-center">
                <div class="googleAccountAgentSettingDialogFooter">
                    <button class="btn-scv-blue margin-r" @click="onApply">適用</button>
                    <button class="btn-scv-white" @click="onClose">閉じる</button>
                </div>
            </div>
        </template>

    </TemplateModalEx>
    <Teleport to="body">
        <GoogleAccountAgentSettingAddDialog v-model:show="googleAccountAgentSettingAddDialog"
            :unconfiguredAgents="unconfiguredAssets" :divisionList="divisionList" @apply="onAddDialogApply" />
        <GoogleAccountAgentSettingEditDialog v-model:show="googleAccountAgentSettingEditDialog"
            :targetAsset="editTargetAsset" :initialSettingData="editInitialSettingsData" @apply="onEditDialogApply" />
        <ErrorReportDialog id="GoogleAccountImportError" :errorReportType="errorReportType"
            :errorRepoortTitle="errorReportTitle" :errorReportText="errorReportText" />
    </Teleport>
</template>

<script setup lang="ts">
import $ from "jquery";
import { ref, computed, watch, shallowRef } from 'vue';

import ListTitleBar from '@/components/ListTitleBar.vue';
import { MenuItemBase } from '@/models/MenuItemBase';
import { ListCtrl } from 'lib-vue-v2/components';
import { TemplateModalEx } from "lib-vue-v2/components";
import { LoadingUtil } from 'lib-vue-v2/util';
import { BootstrapUtil } from "lib-vue-v2/util";
import { SearchColumnData, SearchColumnType } from '@/models/SearchColumnData';
import useGetDivisionDataPackage from '@/views/Mobile/Android/GoogleAccountSetting/package/useGetDivisionDataPackage';
import useGetAndroidAssetPackage from '@/views/Mobile/Android/GoogleAccountSetting/package/useGetAndroidAssetPackage';
import useGetAndroidAgentSettingPackage from '@/views/Mobile/Android/GoogleAccountSetting/package/useGetAndroidAgentSettingPackage';
import useUpdateAndroidAgentSettingPackage from '@/views/Mobile/Android/GoogleAccountSetting/package/useUpdateAndroidAgentSettingPackage';
import { AndroidSimpleAsset } from '@/views/Mobile/Android/GoogleAccountSetting/model/AndroidAsset';
import { AndroidAgentSetting } from '@/views/Mobile/Android/GoogleAccountSetting/model/AndroidAgentSetting';
import { useUserSettingStore } from '@/models/Store/UserSettingStore';
import { useSelectedDivisionStore } from "@/models/Store/DivisionStore";
import { cloneDeep } from 'lodash';
import DivisionArea from '@/components/DivisionArea.vue';
import * as schemaConstants from '@/models/Mobile/Android/AndroidManagementV1SchemaConstants';
import { DivisionData, DivisionDBData } from '@/models/DivisionData';
import { AMAPI_Policy, AMAPI_WorkAccountSetupConfig } from '@/models/Mobile/Android/AndroidManagementV1Schema';
import GoogleAccountAgentSettingAddDialog, { ApplyPayload } from '@/views/Mobile/Android/GoogleAccountSetting/GoogleAccountAgentSettingAddDialog.vue';
import GoogleAccountAgentSettingEditDialog from '@/views/Mobile/Android/GoogleAccountSetting/GoogleAccountAgentSettingEditDialog.vue';
import { EXPORTFILENAME } from '@/views/Mobile/Android/GoogleAccountSetting/GoogleAccountAgentSettingDialogDefinition';
import { executeExport, ExportColumnData } from "@/util/ExportUtil";
import { MessageUtil } from '@/models/Message/MessageUtil';
import { MessageText } from '@/models/Message/MessageText';
import ErrorReportDialog from '@/components/ErrorReportDialog.vue';
import { ErrorReportType } from '@/models/ErrorReport/ErrorReportType';
import { useMessageBox } from "lib-vue-v2/composables";
import { ExecuteImport } from "@/views/Mobile/Android/GoogleAccountSetting/GoogleAccountAgentSettingImport"
import { Resource as CommonResource } from "@/models/Message/Resource/CommonResource";
import { MessageBoxButtonID } from 'lib-vue-v2/components';

// --- Define ---
// ハンバーガーメニュー
const RIGHT_MENU_ITEM_IMPORT = 0;    // インポート
const RIGHT_MENU_ITEM_EXPORT = 1;    // エクスポート

// リスト定義
const GOOGLE_ACCOUNT_SETTING_COLUMN: Array<SearchColumnData> = [
    { columnID: 0, dataType: SearchColumnType.number, text: "端末機No." },
    { columnID: 1, dataType: SearchColumnType.string, text: "端末機名" },
    { columnID: 2, dataType: SearchColumnType.string, text: "コンピューター名" },
    { columnID: 3, dataType: SearchColumnType.string, text: "部署名" },
    { columnID: 4, dataType: SearchColumnType.string, text: "Googleアカウントでのログイン" },
    { columnID: 5, dataType: SearchColumnType.string, text: "Googleアカウント" },
];

// ハンバーガーメニューItem
const selectRightMenuItemList = ref<Array<MenuItemBase>>([
    new MenuItemBase(RIGHT_MENU_ITEM_IMPORT, "インポート"),
    new MenuItemBase(RIGHT_MENU_ITEM_EXPORT, "エクスポート")
]);

interface Props {
    show: boolean;
    onPremises: boolean;
}
const props = withDefaults(defineProps<Props>(), {});

interface Emits {
    (event: "update:show", newValue: boolean): void;
}
const emit = defineEmits<Emits>();

// 表示非表示の切り替え
const refShow = ref(false);
const computedShow = computed({
    get: () => refShow.value,
    set: (newValue: boolean) => emit("update:show", newValue)
});

// --- ref ---
const refTitleBar = ref<InstanceType<typeof ListTitleBar> | null>(null);
const refGoogleAccountListCtrl = ref<InstanceType<typeof ListCtrl> | null>(null);
const divSettings = ref<DivisionData>(new DivisionData());

// 編集ダイアログの表示／非表示
const googleAccountAgentSettingAddDialog = ref(false);
const googleAccountAgentSettingEditDialog = ref(false);
const loading = ref(false);

// メッセージ用
const { showMessageBox } = useMessageBox();

// 選択部署回り
const selectedDivisionId = ref(0);
const isInheritDivision = ref(false);

// store
const userSettingStore = useUserSettingStore();
const selectedDivisionStore = useSelectedDivisionStore();

// api package
const getDivisionDataPackage = useGetDivisionDataPackage();
const getAndroidAssetPackage = useGetAndroidAssetPackage();
const getAndroidAgentSettingPackage = useGetAndroidAgentSettingPackage();
const updateAndroidAgentSettingPackage = useUpdateAndroidAgentSettingPackage();

// data
const isDirty = ref(false);

// data from api
const assetMapByNo = shallowRef(new Map<number, AndroidSimpleAsset>());
const orgAgentSettings = ref<AndroidAgentSetting[]>([]);
const agentSettings = shallowRef<AndroidAgentSetting[]>([]);

const divisionList = ref<Array<DivisionDBData>>([]);

// 表示情報の監視
watch(() => props.show, async () => {
    if (props.show) {
        resetData();
        refShow.value = true;
        let result = await initData();
        if (!result) {
            refShow.value = false;
            emit("update:show", false);
        }
    } else {
        refShow.value = false;
    }
}, { immediate: true });

// 初期化
const initData = async (): Promise<boolean> => {
    try {
        loading.value = true;
        refTitleBar.value?.SearchStart("");
        if (!await loadDivisionSetting()) return false;
        if (!await loadAndroidAgentSetting()) return false;
        if (!await loadAndroidAsset()) return false;
        // 選択部署を取得
        selectedDivisionStore.setDefaultDivision(userSettingStore.divisionId, userSettingStore.divisionName);// オンプレonly
        const selectedDivision = await selectedDivisionStore.getDivisionInfo();
        selectedDivisionId.value = selectedDivision.divisionId;
        isInheritDivision.value = selectedDivision.enableChildDivision;
        updateList();
        return true;
    }
    finally {
        loading.value = false;
    }
}

// 初期化前に戻す
const resetData = (): void => {
    isDirty.value = false;
    assetMapByNo.value.clear();
    orgAgentSettings.value = [];
    agentSettings.value = [];
    divisionList.value = [];
    updateList();
}

// DivisionData情報取得
const loadDivisionSetting = async (): Promise<boolean> => {
    const [success, divisions] = await getDivisionDataPackage.executeAsync();
    if (success) {
        divisionList.value = divisions;
        divSettings.value.Load(divisions);
    }
    return success;
}


// AndroidAgentSetting情報取得
const loadAndroidAgentSetting = async (): Promise<boolean> => {
    const [success, settings] = await getAndroidAgentSettingPackage.executeAsync();
    if (!success) return success;
    agentSettings.value = settings;
    orgAgentSettings.value = cloneDeep(settings);
    return success;
}

// AndroidAsset情報取得
const loadAndroidAsset = async (): Promise<boolean> => {
    const viewableDivisionIds = userSettingStore.getViewableDivisionIds;
    if (viewableDivisionIds === null) return false;
    const [result, assets] = await getAndroidAssetPackage.executeAsync(viewableDivisionIds, true);
    if (!result) return result;
    const newMap = new Map<number, AndroidSimpleAsset>();
    assets.forEach(asset => {
        newMap.set(asset.agentNo, asset);
    });
    assetMapByNo.value = newMap;
    return result;
}

// 適用ボタン押下
const onApply = async (): Promise<void> => {
    const buttonId = await showMessageBox(CommonResource.CommonResource.getInstance().getMsgInfo(CommonResource.MsgID.Common_ConfirmApplySetting));
    if (MessageBoxButtonID.IDYES !== buttonId) return;
    const result = await updateAndroidAgentSettingPackage.executeAsync(orgAgentSettings.value, agentSettings.value);
    if (!result) return;
    emit("update:show", false);
}

// 閉じるボタン押下
const onClose = async (): Promise<void> => {
    if (isDirty.value) {
        const buttonId = await showMessageBox(CommonResource.CommonResource.getInstance().getMsgInfo(CommonResource.MsgID.Common_ConfirmNotApplySetting));
        if (MessageBoxButtonID.IDYES !== buttonId) return;
    }
    emit("update:show", false);
}

// --- 編集ダイアログに渡すデータ ---
const editTargetAsset = ref<AndroidSimpleAsset | undefined>(undefined);
const editInitialSettingsData = ref<AMAPI_WorkAccountSetupConfig | undefined>(undefined);

// --- Computed ---
const selectCount = computed(() => {
    if (refGoogleAccountListCtrl.value === null) return 0;
    return refGoogleAccountListCtrl.value.getSelectListIndex().length;
});

const rowCount = computed(() => {
    if (refGoogleAccountListCtrl.value === null) return 0;
    return refGoogleAccountListCtrl.value.getDisplayListIndex().length;
});

// 「追加」ボタンのモード
const buttonActiveTypeForAdd = computed(() => {
    if (loading.value) return 5;
    return agentSettings.value.length !== assetMapByNo.value.size ? 0 : 5;
});

// まだ設定されていない端末（追加可能な端末）のリストを算出
const unconfiguredAssets = computed(() => {
    const configuredAgentNos = new Set(agentSettings.value.map(s => s.agentNo));

    const results = [];
    for (const asset of assetMapByNo.value.values()) {
        if (!configuredAgentNos.has(asset.agentNo)) {
            results.push({
                agentNo: asset.agentNo,
                agentName: asset.agentName,
                computerName: asset.computerName,
                divisionId: asset.divisionId,
                divisionName: asset.divisionName
            });
        }
    }

    return results.sort((a, b) => a.agentNo - b.agentNo);
});

// 選択された部署情報に基づいて表示する設定リストをフィルタリング
const displayAgentSettings = computed(() => {
    if (divisionList.value.length === 0 || assetMapByNo.value.size === 0) return [];

    const targetDivisionIds: number[] = [];

    if (isInheritDivision.value) {
        // 配下全表示の場合、ツリーから配下すべての部署IDを取得
        const searchDivision = divSettings.value.GetDivisionData(selectedDivisionId.value);
        if (searchDivision) {
            searchDivision.GetAllDivisionID(targetDivisionIds);
        } else {
            targetDivisionIds.push(selectedDivisionId.value);
        }
    } else {
        // 指定部署のみの場合
        targetDivisionIds.push(selectedDivisionId.value);
    }

    const divisionIdSet = new Set(targetDivisionIds);

    // agentSettings のうち、所属部署が targetDivisionIds に含まれるものだけを抽出
    return agentSettings.value.filter(setting => {
        const asset = assetMapByNo.value.get(setting.agentNo);
        if (!asset) return false;
        return divisionIdSet.has(asset.divisionId);
    });
});
watch(displayAgentSettings, () => {
    updateList();
});

// 追加
const onAdd = () => {
    googleAccountAgentSettingAddDialog.value = true;
}

// 編集
const onEdit = () => {
    if (!refGoogleAccountListCtrl.value) return;

    // 現在選択されている行のインデックスを取得
    const selectedIndices = refGoogleAccountListCtrl.value.getSelectListIndex() as number[];
    if (selectedIndices.length === 0) return;

    const targetIndex = selectedIndices[0];

    const settingObj = displayAgentSettings.value[targetIndex];
    if (!settingObj) return;

    const asset = assetMapByNo.value.get(settingObj.agentNo);
    if (!asset) return;

    if (!settingObj.setting || !settingObj.setting.workAccountSetupConfig) return;
    const setting = cloneDeep(settingObj.setting.workAccountSetupConfig);

    editTargetAsset.value = asset;
    editInitialSettingsData.value = setting;
    googleAccountAgentSettingEditDialog.value = true;
}

// 削除
const onDelete = () => {
    if (!refGoogleAccountListCtrl.value) return;

    // 選択された表示行のインデックスを取得
    const selectedIndices = refGoogleAccountListCtrl.value?.getSelectListIndex() as number[];
    if (selectedIndices.length === 0) return;

    // 削除対象の端末機Noを特定する
    const agentNosToDelete = new Set<number>();
    selectedIndices.forEach(index => {
        const setting = displayAgentSettings.value[index];
        if (setting) {
            agentNosToDelete.add(setting.agentNo);
        }
    });

    // マスターの配列から、特定した端末機Noを省いて再代入する
    agentSettings.value = agentSettings.value.filter(s => !agentNosToDelete.has(s.agentNo));

    isDirty.value = true;
}

// 検索
const search = (searchText: string = ""): void => {
    refGoogleAccountListCtrl.value?.search(searchText);
}

// ハンバーガーメニュー押下
const onClickRightMenuItem = (index: number): void => {
    switch (index) {
        case RIGHT_MENU_ITEM_IMPORT:
            setTimeout(() => { doImport(); }, 100);
            break;
        case RIGHT_MENU_ITEM_EXPORT:
            setTimeout(() => { doExport(); }, 100);
            break;
    }
}

// 部署選択の変更検知
const onDivisionSelectChange = (_divisionId: number, divisionName: string, _isInheritDivision: boolean): void => {
    if (!props.show) return;
    selectedDivisionId.value = _divisionId;
    isInheritDivision.value = _isInheritDivision;
    selectedDivisionStore.setDivisionInfo(_divisionId, divisionName, _isInheritDivision);
}

// リスト更新
const updateList = () => {
    refGoogleAccountListCtrl.value?.updateList(displayAgentSettings.value.length);
}

// リスト作成処理(テキスト)
const getCellText = (columnId: number, index: number, isexport: boolean): string => {
    if (columnId < 4) {
        var agent = assetMapByNo.value.get(displayAgentSettings.value[index].agentNo);
        if (agent === undefined) return "";
        switch (columnId) {
            case 0:
                return String(agent.agentNo);
            case 1:
                return agent.agentName;
            case 2:
                return agent.computerName;
            case 3:
                return agent.divisionName;
        }
    }
    else {
        const setting = displayAgentSettings.value[index].setting;
        const workAccountConfig = setting.workAccountSetupConfig;
        if (!workAccountConfig) return "";
        switch (columnId) {
            case 4:
                if (!schemaConstants.isAMAPI_AuthenticationType_Enums(workAccountConfig.authenticationType)) return "";
                return workAccountConfig.authenticationType === schemaConstants.AMAPI_AuthenticationType_Enums.googleAuthEnticated ? "必須" : "任意";
            case 5: {
                if (isexport) return workAccountConfig.requiredAccountEmail ?? "";
                const isAuthRequired = workAccountConfig.authenticationType === schemaConstants.AMAPI_AuthenticationType_Enums.googleAuthEnticated;
                if (isAuthRequired) return workAccountConfig.requiredAccountEmail || "<指定なし>";
                else return "-";
            }
        }
    }
    return "";
}

// インポートのエラーレポートタイトル
const errorReportTitle = ref("インポート");
// インポートのエラーレポート内容
const errorReportText = ref("");
// インポートのエラーレポート種別
const errorReportType = ref(ErrorReportType.Failed);
// インポート実行
const doImport = (): void => {
    $("#importFileImput_GooglAccountImport").click();
};
// インポート
const onFileSelected = async (event: any): Promise<void> => {
    try {
        // 選択された File の情報を取得
        const files = event.target.files;
        if (files === undefined || files === null || files.length <= 0) {
            MessageUtil.MessageBox(MessageText.importFileIsNothing, null, null, MessageUtil.MB_ICON_WARNING);
            return;
        }

        LoadingUtil.Start();
        const allowableAgentNos = new Set(assetMapByNo.value.keys());
        const ret = await ExecuteImport(files[0] as File, agentSettings.value, allowableAgentNos);
        LoadingUtil.Stop();

        if (ret.result) {
            // 成功
            showMessageBox(
                CommonResource.CommonResource.getInstance().getMsgInfo(
                    CommonResource.MsgID.Import_CompleteInfo
                )
            );
            agentSettings.value = ret.data
        }
        else if (0 < ret.errorReport.length) {
            // 失敗（エラーレポートあり）
            // エラーレポート表示
            errorReportText.value = ret.errorReport;
            BootstrapUtil.showModal("GoogleAccountImportError");
        }
        else {
            // 失敗（エラーレポートなし）
            // メッセージはUtilで出力済みのため終了
        }
    }
    finally {
        $("#importFileImput_GooglAccountImport").val('');
    }
}

// エクスポート実行
const doExport = ((): void => {
    const filename = EXPORTFILENAME;
    const indexs = refGoogleAccountListCtrl.value?.getDisplayListIndex() as Array<number>;
    const exportColums = ((): ExportColumnData[] => {
        return GOOGLE_ACCOUNT_SETTING_COLUMN.map(e => { return { columnId: e.columnID, columnText: e.text } })
    })();
    executeExport(filename, indexs, exportColums, exportFiledText, { showCompleteMessage: props.onPremises });
});

// エクスポートデータ出力関数
const exportFiledText = ((index: number, columnId: number): [boolean, string] => {
    // no data
    if (displayAgentSettings.value.length <= index) return [false, ""];
    return [true, getCellText(columnId, index, true)];
});

// 追加ダイアログの「適用」が押された際のハンドラ
const onAddDialogApply = (payload: ApplyPayload) => {
    // 追加ダイアログ側で選択されていた部署情報を大元の親に反映する
    selectedDivisionId.value = payload.divisionData.divisionId;
    isInheritDivision.value = payload.divisionData.enableChildDivision;

    // Storeにも記憶させる
    const divisionName = divisionList.value.find(d => d.divisionID === payload.divisionData.divisionId)?.divisionName || "";
    selectedDivisionStore.setDivisionInfo(payload.divisionData.divisionId, divisionName, payload.divisionData.enableChildDivision);

    // 追加ダイアログ側で入力された「Googleアカウント設定情報」
    const selectedAgentIds = payload.selectedAgentIds;
    const settingData = payload.settingData;
    const nextSettings = [...agentSettings.value];
    if (!selectedAgentIds || selectedAgentIds.size === 0) return;
    selectedAgentIds.forEach(agentNo => {
        const newPolicy = new AMAPI_Policy();
        newPolicy.workAccountSetupConfig = cloneDeep(settingData);

        const newSetting: AndroidAgentSetting = {
            agentNo: agentNo,
            setting: newPolicy
        };
        nextSettings.push(newSetting);
    });
    nextSettings.sort((a, b) => a.agentNo - b.agentNo);
    agentSettings.value = nextSettings;
    updateList();
    isDirty.value = true;
}

// 編集ダイアログの「適用」が押された際の更新ハンドラ
const onEditDialogApply = (newSettingData: AMAPI_WorkAccountSetupConfig) => {
    if (!refGoogleAccountListCtrl.value) return;
    const selectedIndices = refGoogleAccountListCtrl.value.getSelectListIndex() as number[];
    if (selectedIndices.length !== 1) return;

    // リアクティブな更新を検知させるため、配列のコピーを作成
    const nextSettings = [...agentSettings.value];

    selectedIndices.forEach(index => {
        // 表示用リストから、更新対象の端末Noを特定する
        const targetSetting = displayAgentSettings.value[index];
        if (targetSetting) {
            // マスター配列(nextSettings)の中から、該当する端末を検索して設定を上書き
            const masterIndex = nextSettings.findIndex(s => s.agentNo === targetSetting.agentNo);
            if (masterIndex >= 0) {
                if (!nextSettings[masterIndex].setting) nextSettings[masterIndex].setting = new AMAPI_Policy();
                nextSettings[masterIndex].setting.workAccountSetupConfig = cloneDeep(newSettingData);
            }
        }
    });

    agentSettings.value = nextSettings;
    updateList();
    isDirty.value = true;
}
</script>

<style lang="scss" scoped>
.googleAccountAgentSettingDialogDetails {
    :deep(.modal-content) {
        width: 960px;
        height: 596px;
        max-width: none;
        max-height: none;
        display: flex;
        flex-direction: column;
        overflow: hidden; // 枠外への飛び出しを防止
    }

    // ボディ部全体を縦フレックスにし、中身を自動配分する
    :deep(.modal-body) {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0; // 高さが潰れたり溢れたりするのを防ぐ
    }

    // ボディ内の伸縮しない要素（固定パーツ）の指定
    :deep(.modal-body) {
        >span {
            flex-shrink: 0;
            display: inline-block;
        }

        .division-area-wrapper {
            flex-shrink: 0;
        }

        // タイトルバー
        >div:not(.google-account-list):not(.division-area-wrapper) {
            flex-shrink: 0;
        }
    }

    .googleAccountAgentSettingDialogFooter {
        display: flex;
        flex-direction: row;
        align-items: center;

        .margin-r {
            margin-right: 24px;
        }
    }
}

// リストコントロールの自動伸縮指定
.google-account-list {
    height:300px;
    overflow: hidden;
}
</style>
